import { useMutation, useQueryClient } from '@tanstack/react-query';

import {
	createDeal,
	updateDeal,
	deleteDeal,
	restoreDeal,
	rescoreDeal,
	bulkDeleteDeals,
	bulkRestoreDeals,
} from '../../api/crmApi';

import { dealsKeys, dealsMutationKeys } from './keys';

export function useCreateDeal() {
	const qc = useQueryClient();
	return useMutation({
		mutationKey: dealsMutationKeys.create,
		mutationFn: createDeal,
		onSuccess: (data) => {
			const deal = data?.deal;
			if (deal?.pipelineId && deal?.stageId) {
				qc.invalidateQueries({ queryKey: ['crm', 'deals', 'byStageInfinite', deal.pipelineId, deal.stageId] });
				qc.invalidateQueries({ queryKey: ['crm', 'deals', 'stageSummaries', deal.pipelineId] });
			}
			qc.invalidateQueries({ queryKey: dealsKeys.lists() });
			if (deal?.crmCompanyId) {
				qc.invalidateQueries({ queryKey: ['crm', 'company', deal.crmCompanyId] });
			}
		},
	});
}

/**
 * Apply a partial update to a deal everywhere it appears in the cache.
 * Handles three cache shapes: array of deals (deals lists), paginated
 * `{ deals: [...] }` (useDealsList), and infinite `{ pages: [{ deals: [...] }] }`
 * (kanban stage pages). Returns a snapshot map for rollback.
 */
function patchDealAcrossCaches(qc, dealId, patch) {
	const previous = [];
	const entries = qc.getQueriesData({ queryKey: dealsKeys.all });
	for (const [key, data] of entries) {
		if (!data) continue;
		let next = data;
		if (Array.isArray(data)) {
			next = data.map((d) => (d.id === dealId ? { ...d, ...patch } : d));
		} else if (Array.isArray(data.deals)) {
			next = { ...data, deals: data.deals.map((d) => (d.id === dealId ? { ...d, ...patch } : d)) };
		} else if (Array.isArray(data.pages)) {
			next = {
				...data,
				pages: data.pages.map((page) => ({
					...page,
					deals: page.deals?.map((d) => (d.id === dealId ? { ...d, ...patch } : d)) ?? page.deals,
				})),
			};
		} else {
			continue;
		}
		if (next !== data) {
			previous.push([key, data]);
			qc.setQueryData(key, next);
		}
	}
	return previous;
}

/**
 * Remove a deal from every list-shaped cache entry where it appears.
 * Mirrors the three cache shapes handled by patchDealAcrossCaches:
 * array of deals, paginated `{ deals: [...] }` (useDealsList), and infinite
 * `{ pages: [{ deals: [...] }] }` (kanban stage pages). Returns a snapshot
 * map for rollback.
 */
function removeDealFromCaches(qc, dealId) {
	const previous = [];
	const entries = qc.getQueriesData({ queryKey: dealsKeys.all });
	for (const [key, data] of entries) {
		if (!data) continue;
		let next = data;
		if (Array.isArray(data)) {
			next = data.filter((d) => d.id !== dealId);
		} else if (Array.isArray(data.deals)) {
			next = { ...data, deals: data.deals.filter((d) => d.id !== dealId) };
		} else if (Array.isArray(data.pages)) {
			next = {
				...data,
				pages: data.pages.map((page) => ({
					...page,
					deals: page.deals?.filter((d) => d.id !== dealId) ?? page.deals,
				})),
			};
		} else {
			continue;
		}
		if (next !== data) {
			previous.push([key, data]);
			qc.setQueryData(key, next);
		}
	}
	return previous;
}

export function useUpdateDeal() {
	const qc = useQueryClient();
	return useMutation({
		mutationKey: dealsMutationKeys.update,
		mutationFn: ({ id, payload }) => updateDeal(id, payload),
		onMutate: async ({ id, payload }) => {
			await qc.cancelQueries({ queryKey: dealsKeys.all });
			const previousDeals = patchDealAcrossCaches(qc, id, payload);
			return { previousDeals };
		},
		onError: (err, vars, context) => {
			for (const [key, data] of context?.previousDeals ?? []) {
				qc.setQueryData(key, data);
			}
		},
		onSettled: (data, err, { id }) => {
			qc.invalidateQueries({ queryKey: dealsKeys.detail(id) });
			qc.invalidateQueries({ queryKey: dealsKeys.allStageSummaries() });
			if (data?.deal?.pipelineId) {
				qc.invalidateQueries({ queryKey: ['crm', 'deals', 'byStageInfinite', data.deal.pipelineId] });
			}
			if (data?.deal?.crmCompanyId) {
				qc.invalidateQueries({ queryKey: ['crm', 'company', data.deal.crmCompanyId] });
			}
		},
	});
}

export function useDeleteDeal() {
	const qc = useQueryClient();
	return useMutation({
		mutationKey: dealsMutationKeys.delete,
		mutationFn: deleteDeal,
		// Optimistically drop the deal from every list cache so rows disappear
		// immediately — invalidation alone leaves stale rows until the refetch lands.
		onMutate: async (id) => {
			await qc.cancelQueries({ queryKey: dealsKeys.all });
			const previousDeals = removeDealFromCaches(qc, id);
			return { previousDeals };
		},
		onError: (err, id, context) => {
			for (const [key, data] of context?.previousDeals ?? []) {
				qc.setQueryData(key, data);
			}
		},
		// Sync with server — delete response lacks pipelineId/stageId for targeted invalidation
		onSettled: () => {
			qc.invalidateQueries({ queryKey: dealsKeys.all });
		},
	});
}

export function useRestoreDeal() {
	const qc = useQueryClient();
	return useMutation({
		mutationKey: dealsMutationKeys.restore,
		mutationFn: restoreDeal,
		// Restore affects trash + all deal views — no pipelineId/stageId available for targeted invalidation
		onSettled: () => {
			qc.invalidateQueries({ queryKey: dealsKeys.all });
		},
	});
}

export function useRescoreDeal() {
	const qc = useQueryClient();
	return useMutation({
		mutationKey: dealsMutationKeys.rescore,
		mutationFn: rescoreDeal,
		onSuccess: (data) => {
			const deal = data?.deal;
			if (!deal) return;
			if (deal.id) {
				qc.invalidateQueries({ queryKey: dealsKeys.detail(deal.id) });
			}
			if (deal.pipelineId) {
				qc.invalidateQueries({ queryKey: ['crm', 'deals', 'stageSummaries', deal.pipelineId] });
				qc.invalidateQueries({ queryKey: ['crm', 'deals', 'byStageInfinite', deal.pipelineId] });
			}
		},
	});
}

function invalidateAfterBulkDealChange(qc) {
	qc.invalidateQueries({ queryKey: dealsKeys.all });
	qc.invalidateQueries({ queryKey: ['crm', 'company'] });
}

export function useBulkDeleteDeals() {
	const qc = useQueryClient();
	return useMutation({
		mutationKey: dealsMutationKeys.bulkDelete,
		mutationFn: bulkDeleteDeals,
		onSettled: () => invalidateAfterBulkDealChange(qc),
	});
}

export function useBulkRestoreDeals() {
	const qc = useQueryClient();
	return useMutation({
		mutationKey: dealsMutationKeys.bulkRestore,
		mutationFn: bulkRestoreDeals,
		onSettled: () => invalidateAfterBulkDealChange(qc),
	});
}
